import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { deleteEntity, updateEntities } from '@redux/entities'

import api from '@functions/api'

import type { ArtworkModel, CreateOrderItemPayload, OrderItemModel } from '@models/types'

import type { AppDispatch } from '@redux/store'
import type {
  Action, ActionResult, DeleteParams, EntityOptions, Errors, FetchRequestPayload, RootReducerState,
} from '@redux/modules/types'

import { removeOrderById, updateOrderTotal } from '@redux/modules/order'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/orderItem/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/orderItem/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/orderItem/FETCH_FAILURE'

const UPDATE_REQUEST = 'realbase/orderItem/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/orderItem/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/orderItem/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/orderItem/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/orderItem/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/orderItem/DELETE_FAILURE'

export type OrderItemModuleState = {
  deleting: boolean,
  errors: Errors,
  loaded: boolean,
  loadedForKeys: string[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: OrderItemModuleState = {
  deleting: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}): Action {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
  }
}

export function fetchSuccess(): Action {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []): Action {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function updateRequest(): Action {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess(): Action {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []): Action {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadOrderItems(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().orderItems.loadedForKeys]
    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/order_items.json', options)
      .then(({ data }: { data: OrderItemModel[] }) => {
        const normalizedJson = normalize(data, Schemas.ORDER_ITEM_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadCompatibleArtworks(
  campaignId: number,
  serviceProductId: number,
  options: EntityOptions = {},
) {
  const mergedOptions = {
    campaign_id: campaignId,
    compatible_with_product_id: serviceProductId,
    include_artwork_preview: true,
    ...options,
  }

  return (dispatch: AppDispatch) => {
    dispatch(fetchRequest())

    const promise = api('/artworks.json', mergedOptions)
      .then(({ data }: { data: ArtworkModel[] }) => {
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateOrderItem(orderItem: OrderItemModel, options: EntityOptions = {}) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      order_item: orderItem,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/order_items/${orderItem.id}.json`, options, config)
      .then(({ data }: { data: OrderItemModel }) => {
        const normalizedJson = normalize(data, Schemas.ORDER_ITEM)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateOrderItemOptions(
  orderItemId: number,
  itemPayload: CreateOrderItemPayload,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      order_item: itemPayload,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/order_items/${orderItemId}/update_options.json`, options, config)
      .then(({ data }: { data: OrderItemModel }) => {
        const normalizedJson = normalize(data, Schemas.ORDER_ITEM)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        // Update Order Total
        // @ts-expect-error TS2339
        if (data.order_total){
          // @ts-expect-error TS2339
          dispatch(updateOrderTotal(data.order_id, data.order_total))
        }

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deleteOrderItem(orderItem: DeleteParams<OrderItemModel>) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/order_items/${orderItem.id}.json`, {}, config)
      .then(({ data }: { data: { id: number } }) => {
        const normalizedJson = normalize(data, Schemas.ORDER_ITEM)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        // Update Order Total
        // @ts-expect-error TS2339
        if (data.order_total){
          // @ts-expect-error TS2339
          dispatch(updateOrderTotal(data.order_id, data.order_total))
        }

        // Remove order if it has no items
        // @ts-expect-error TS2339
        if (data.item_count === 0){
          // @ts-expect-error TS2339
          dispatch(removeOrderById(data.order_id))
        }

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function duplicateOrderItem(
  orderItem: Partial<OrderItemModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'POST',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/order_items/${orderItem.id}/duplicate.json`, options, config)
      .then(({ data }: { data: OrderItemModel }) => {
        const normalizedJson = normalize(data, Schemas.ORDER_ITEM)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        // Update Order Total
        // @ts-expect-error TS2339
        if (data.order_total){
          // @ts-expect-error TS2339
          dispatch(updateOrderTotal(data.order_id, data.order_total))
        }

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function artworkBuilderConfig(orderItemId: number) {
  return (dispatch: AppDispatch): Promise<ActionResult<OrderItemModel>> => {
    // Set Loading
    dispatch(fetchRequest())

    const promise = api(`/order_items/${orderItemId}/artwork_builder_config.json`)
      .then(({ data }: { data: OrderItemModel }) => {
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function assignExistingArtwork(
  orderItemId: number,
  existingArtworkId: number,
  options: EntityOptions = {},
) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      existing_artwork_id: existingArtworkId,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/order_items/${orderItemId}/assign_existing_artwork.json`, options, config)
      .then(({ data }: { data: OrderItemModel }) => {
        const normalizedJson = normalize(data, Schemas.ORDER_ITEM)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: OrderItemModuleState = initialState,
  action: Action = { type: '' },
): OrderItemModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
